// src/prototype/components/EAEvaluationRunOverlay.tsx
// ============================================================
// EVALUATION-RUN INTERSTITIAL
//
// Shown once when Evaluate is entered after a personalization change. Four honest stage
// labels appear in sequence over ~5s. Click anywhere to skip.
//
// Deliberately NOT here: a percentage, a progress bar, or an elapsed-time readout. The stage
// progression is the only signal, because it is the only one that corresponds to something
// real. An invented "73%" on a screen whose whole argument is auditability would be the
// wrong trade.
// ============================================================

import React from 'react';
import { Check, Loader2 } from 'lucide-react';
import { EVAL_STAGES } from '../hooks/useEvaluationRun';

export interface EAEvaluationRunOverlayProps {
  visibleStages: number;
  onSkip: () => void;
}

export const EAEvaluationRunOverlay: React.FC<EAEvaluationRunOverlayProps> = ({
  visibleStages,
  onSkip,
}) => (
  <div
    role="button"
    tabIndex={0}
    aria-label="Evaluation in progress — click to skip"
    onClick={onSkip}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') onSkip(); }}
    data-testid="evaluation-run-overlay"
    className="fixed inset-0 z-[60] bg-[#18365E]/95 flex items-center justify-center cursor-pointer"
  >
    <div className="max-w-md w-full mx-4 text-left">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-white/50 mb-4">
        Evaluating your portfolio
      </p>

      <ul className="space-y-3">
        {EVAL_STAGES.map((stage, i) => {
          const shown = i < visibleStages;
          const done = i < visibleStages - 1;
          if (!shown) return null;
          return (
            <li key={stage} className="flex items-center gap-3 text-white">
              {done
                ? <Check className="w-4 h-4 text-emerald-300 shrink-0" />
                : <Loader2 className="w-4 h-4 text-[#FF6E4C] shrink-0 animate-spin" />}
              <span className={`text-sm ${done ? 'text-white/60' : 'font-semibold'}`}>{stage}</span>
            </li>
          );
        })}
      </ul>

      <p className="text-[11px] text-white/40 mt-6">Click anywhere to skip</p>
    </div>
  </div>
);

export default EAEvaluationRunOverlay;
